import { useEffect, useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    ["Who We Are", "who-we-are"],
    ["What We Do", "what-we-do"],
    ["Focus", "focus"],
    ["Philosophy", "philosophy"],
    ["About", "about"],
    ["Markets", "markets"],
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
                scrolled || open
                    ? "border-b border-white/10 bg-[#0A1224]/85 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("top")}
                    className="flex items-center"
                >
                    <img
                        src="/images/logo.png"
                        alt="Value Add Management"
                        className="h-9 w-auto"
                    />
                </button>
                <nav className="hidden items-center gap-7 lg:flex">
                    {LINKS.map(([label, id]) => (
                        <button
                            key={id}
                            data-testid={`nav-link-${id}`}
                            onClick={() => go(id)}
                            className="text-[12px] font-semibold uppercase tracking-[0.16em] text-slate-300 transition-colors hover:text-[#1BA8E0]"
                        >
                            {label}
                        </button>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-contact-cta"
                        onClick={() => go("contact")}
                        className="group hidden items-center gap-2 rounded-full bg-[#1BA8E0] px-5 py-2.5 text-sm font-semibold text-[#06121F] transition-colors duration-300 hover:bg-white sm:inline-flex"
                    >
                        Contact Us
                        <ArrowUpRight
                            size={16}
                            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="text-white lg:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="border-t border-white/10 bg-[#0A1224]/95 px-5 py-6 backdrop-blur-xl lg:hidden">
                    <div className="flex flex-col gap-5">
                        {LINKS.map(([label, id]) => (
                            <button
                                key={id}
                                data-testid={`nav-mobile-link-${id}`}
                                onClick={() => go(id)}
                                className="text-left text-sm font-semibold uppercase tracking-[0.16em] text-slate-200"
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            data-testid="nav-mobile-contact-cta"
                            onClick={() => go("contact")}
                            className="mt-2 inline-flex w-max items-center gap-2 rounded-full bg-[#1BA8E0] px-5 py-2.5 text-sm font-semibold text-[#06121F]"
                        >
                            Contact Us <ArrowUpRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
